import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "@/lib/api";
import { staffSessionActions } from "./staffSessionSlice";

export type UserRole = "admin" | "data_staff" | "intake_staff" | "proofer" | "guest";

export interface RoleState {
  role: UserRole | null;
  loading: boolean;
  error: string | null;
  requestId?: string | null;
}

const initialState: RoleState = {
  role: null,
  loading: false,
  error: null,
};

/**
 * Fetches the user's role from the backend.
 * The backend resolves this from Entra ID security groups.
 *
 * Expected API shape:
 *   GET /api/users/me/role
 *   Response: { "role": "admin" | "data_staff" | "intake_staff" | "proofer" | "guest" }
 */
export const fetchUserRole = createAsyncThunk<UserRole>(
  "role/fetchUserRole",
  async (_, { rejectWithValue }) => {
    try {
      const data = await api.get<{ role: UserRole }>("/api/users/me/role");
      return data.role;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch user role"
      );
    }
  }
);

const roleSlice = createSlice({
  name: "role",
  initialState,
  reducers: {
    clearRole() { return initialState; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(staffSessionActions.scopeRevoked, () => initialState)
      .addCase(staffSessionActions.contextAccepted, (_state, action) => ({
        role: action.payload.role, loading: false, error: null, requestId: null,
      }))
      .addCase(fetchUserRole.pending, (state, action) => {
        state.requestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserRole.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.requestId = null;
        state.role = action.payload;
        state.loading = false;
      })
      .addCase(fetchUserRole.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.requestId = null;
        state.loading = false;
        state.error = action.payload as string;
        // Fall back to guest so the app doesn't get stuck
        state.role = "guest";
      });
  },
});

export const { clearRole } = roleSlice.actions;
export default roleSlice.reducer;
